import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PersonaTipoUtenteEnum } from '../../../../api';
import apiManager from '../../../../api/api-manager';
import { parsePhoneNumberForServer } from '../../../../api/parsers/commons-api-parsers';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import {
  RetrieveUsernameError,
  RetrieveUsernameOptions,
  RetrieveUsernameParams,
  RetrieveUsernameState,
} from './retrieve-username-types';
import {
  getRetrieveUsernameError,
  verifyRetrieveUsernameCredentials,
} from './retrieve-username-utils';

const initialState: RetrieveUsernameState = {
  retrieveUsernameRequestStatus: 'idle',
  selectedOption: RetrieveUsernameOptions.BySMS,
  multipleMatchesFound: false,
  userType: '',

};

export const retrieveUsername = createAsyncThunk<
  string,
  RetrieveUsernameParams,
  AsyncThunkConfig<RetrieveUsernameError>
>('retrieve username', async (params: RetrieveUsernameParams, thunkApi) => {
  try {
    const state = thunkApi.getState().features.retrieveUsername;

    const validationError = verifyRetrieveUsernameCredentials(params, state);
    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    await apiManager.loginApi.recuperaUsername({
      email:
        state.selectedOption === RetrieveUsernameOptions.ByMail
          ? state.userEmail
          : undefined,
      cellulare:
        state.selectedOption === RetrieveUsernameOptions.BySMS
          ? parsePhoneNumberForServer(state.userPhoneNumber)
          : undefined,
      codiceUtente: params.userCode,
      codiceAccordoQuadro: params.squareAgreementCode,
    });
    return null;
  } catch (error) {
    const state = thunkApi.getState().features.retrieveUsername;
    return thunkApi.rejectWithValue(
      getRetrieveUsernameError(error, state, params)
    );
  }
});

const retrieveUsernameSlice = createSlice({
  name: 'features/retrieve-username',
  initialState,
  reducers: {
    resetRetrieveUsernameState(state) {
      state.retrieveUsernameError = undefined;
      state.userEmail = undefined;
      state.userPhoneNumber = undefined;
      state.retrieveUsernameRequestStatus = 'idle';
      state.selectedOption = RetrieveUsernameOptions.BySMS;
    },
    clearRetrieveUsernameError(state) {
      state.retrieveUsernameError = undefined;
      state.retrieveUsernameRequestStatus = 'idle';
    },
    clearMultipleMatchesFlag(state) {
      state.multipleMatchesFound = false;
    },
    setSelectedOption(state, action: PayloadAction<RetrieveUsernameOptions>) {
      state.selectedOption = action.payload;
    },
    setSelectedUserType(state, action: PayloadAction<PersonaTipoUtenteEnum>) {
      state.userType = action.payload;
      state.selectedOption =
        RetrieveUsernameOptions[action.payload === 'PF' ? 'BySMS' : 'ByMail'];
    },
    setUserEmail(state, action: PayloadAction<string>) {
      state.userEmail = action.payload;
    },
    setUserPhoneNumber(state, action: PayloadAction<string>) {
      state.userPhoneNumber = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(retrieveUsername.pending, (state) => {
      state.retrieveUsernameRequestStatus = 'loading';
    });
    builder.addCase(retrieveUsername.fulfilled, (state) => {
      state.retrieveUsernameRequestStatus = 'succeeded';
    });
    builder.addCase(retrieveUsername.rejected, (state, action) => {
      /*
				Multiple matches case is managed inside the thunk by using a dedicated property,
				since is not a real error, but just a different case that must be managed in a
				proper way
      		*/
      if (action.payload === 'MULTIPLE_MATCH') {
        state.retrieveUsernameRequestStatus = 'idle';
        state.multipleMatchesFound = true;
      } else {
        state.retrieveUsernameError = action.payload;
        state.retrieveUsernameRequestStatus = 'failed';
      }
    });
  },
});

export const {
  resetRetrieveUsernameState,
  clearRetrieveUsernameError,
  setSelectedOption,
  setUserEmail,
  setUserPhoneNumber,
  clearMultipleMatchesFlag,
  setSelectedUserType,
} = retrieveUsernameSlice.actions;

export default retrieveUsernameSlice.reducer;
